import React, { useState, useRef, useEffect } from 'react';
import { JUA_COLOR_PALETTE } from '../utils/colorUtils';

interface ColorPickerProps {
  currentColor: string;
  onColorChange: (color: string) => void;
  onClose: () => void;
  isOpen: boolean;
}

export function ColorPicker({ currentColor, onColorChange, onClose, isOpen }: ColorPickerProps) {
  const [customColor, setCustomColor] = useState(currentColor);
  const pickerRef = useRef<HTMLDivElement>(null);

  // Close picker when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        // Check if the click is on the color button itself (to allow toggling)
        const target = event.target as Element;
        const isColorButton = target.closest('[data-color-button]');
        if (!isColorButton) {
          onClose();
        }
      }
    }

    if (isOpen) {
      // Use a small delay to avoid conflicts with the button click
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 10);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handlePresetColorClick = (color: string) => {
    setCustomColor(color);
    onColorChange(color);
    // Small delay to ensure the color change is processed before closing
    setTimeout(() => onClose(), 50);
  };

  const handleCustomColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const color = event.target.value;
    setCustomColor(color);
    onColorChange(color);
  };

  return (
    <div
      ref={pickerRef}
      className="fixed z-[9999] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-4 min-w-[240px]"
      style={{
        // Position relative to the viewport to ensure it's always visible
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        // Ensure it stays on top of everything
        zIndex: 9999,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Preset Colors */}
      <div className="mb-4">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Preset Colors</h4>
        <div className="grid grid-cols-6 gap-2">
          {JUA_COLOR_PALETTE.map((color, index) => (
            <button
              key={index}
              className={`w-8 h-8 rounded-lg border-2 transition-all duration-200 hover:scale-110 ${
                currentColor === color
                  ? 'border-gray-400 dark:border-gray-500'
                  : 'border-gray-200 dark:border-gray-600'
              }`}
              style={{ backgroundColor: color }}
              onClick={() => handlePresetColorClick(color)}
              title={color}
            />
          ))}
        </div>
      </div>

      {/* Custom Color */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Custom Color</h4>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={customColor}
            onChange={handleCustomColorChange}
            className="w-8 h-8 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
          />
          <input
            type="text"
            value={customColor}
            onChange={(e) => {
              setCustomColor(e.target.value);
              if (/^#[0-9A-F]{6}$/i.test(e.target.value)) {
                onColorChange(e.target.value);
              }
            }}
            className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            placeholder="#000000"
          />
        </div>
      </div>
    </div>
  );
}
